import { writable } from 'svelte/store';
import type { QueueStorage } from './storage';
import { TransferError, type AdapterLoader, type FileRecord, type Json, type Receipt, type Transfer, type TransferAdapter, type TransferContext, type TransferInput, type TransferProgress } from './types';

const live = (job: Transfer) => !!job.lease && (job.leaseUntil || 0) > Date.now();
const eligible = (job: Transfer) => ['waiting', 'preparing', 'uploading', 'confirming', 'processing'].includes(job.state);
const fileInfo = (file: File) => ({ name: file.name, type: file.type, size: file.size });
const copyData = (data: Json): Json => {
  const validate = (value: unknown): void => {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return;
    if (typeof value === 'number' && Number.isFinite(value)) return;
    if (Array.isArray(value)) { value.forEach(validate); return; }
    if (typeof value === 'object' && Object.prototype.toString.call(value) === '[object Object]') { Object.values(value).forEach(validate); return; }
    throw new Error('Übertragungsdaten müssen JSON-Daten sein.');
  };
  validate(data);
  return JSON.parse(JSON.stringify(data));
};

export type QueueEnvironment = {
  identity: () => Promise<string | null>;
  online: () => boolean;
  changed: () => void;
  completed: (job: Transfer) => void;
  progress?: (event: TransferProgress) => void;
};

/** One engine per document, durable fenced leases coordinate all tabs. No feature knowledge. */
export class TransferEngine {
  readonly jobs = writable<Transfer[]>([]);
  readonly error = writable('');
  private owner = '';
  private epoch = 0;
  private records: Transfer[] = [];
  private unsafe = new Map<string, { job: Transfer; files: FileRecord }>();
  private active = new Map<string, AbortController>();
  private pumping = false;
  private requested = false;
  private refreshSequence = 0;
  private authSequence = 0;
  private cursor = '';
  constructor(private storage: QueueStorage, private loaders: Record<string, AdapterLoader>, private env: QueueEnvironment) {}

  private publish() {
    const jobs = new Map(this.records.filter(job => job.owner === this.owner).map(job => [job.id, job]));
    for (const [id, item] of Array.from(this.unsafe)) if (item.job.owner === this.owner) jobs.set(id, item.job);
    this.jobs.set(Array.from(jobs.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)));
  }
  setOwner(owner: string | null) {
    if (this.owner === (owner || '')) return;
    this.owner = owner || ''; this.epoch++;
    for (const controller of Array.from(this.active.values())) controller.abort();
    this.records = []; this.publish();
  }
  pause() { this.authSequence++; this.setOwner(null); }
  async authenticate() {
    const sequence = ++this.authSequence;
    const owner = await this.env.identity();
    if (sequence !== this.authSequence) throw new TransferError('Anmeldung wird erneut geprüft.');
    this.setOwner(owner);
    if (!owner) throw new TransferError('Bitte erneut anmelden. Deine Übertragungen bleiben auf diesem Gerät.', true, true);
    this.error.set('');
    return owner;
  }
  async refresh() {
    const sequence = ++this.refreshSequence, epoch = this.epoch;
    const records = await this.storage.list();
    if (sequence !== this.refreshSequence || epoch !== this.epoch) return;
    this.records = records.filter(job => job.owner === this.owner).map(job => {
      const previous = this.records.find(item => item.id === job.id);
      return previous?.lease && previous.lease === job.lease && ['uploading', 'confirming'].includes(previous.state)
        ? { ...job, progress: previous.progress, state: previous.state } : job;
    });
    this.publish();
  }
  receiveProgress(event: TransferProgress) {
    if (event.owner !== this.owner || !event.lease) return;
    this.records = this.records.map(job => job.id === event.id && job.lease === event.lease
      ? { ...job, progress: event.progress, state: event.state } : job);
    this.publish();
  }
  private async changed() { this.env.changed(); await this.refresh(); }
  private requireOwner() {
    if (!this.owner) throw new Error('Bitte zunächst mit Verbindung anmelden.');
    return this.owner;
  }
  private assertScope(job: Transfer | undefined, feature: string): asserts job is Transfer {
    if (!job || job.owner !== this.requireOwner() || job.feature !== feature) throw new Error('Übertragung nicht verfügbar.');
  }
  async save(feature: string, input: TransferInput, updateDraft = false) {
    if (!this.loaders[feature]) throw new Error('Feature hat keine Übertragungsadapter.');
    const owner = this.requireOwner(), now = new Date().toISOString();
    const files = [...(input.files || [])];
    const job: Transfer = { id: input.id || crypto.randomUUID(), feature, owner, type: input.type, data: copyData(input.data),
      title: input.title, group: input.group, href: input.href, parentId: input.parentId,
      files: files.map(fileInfo), state: input.draft ? 'draft' : 'waiting', progress: null, attempts: 0, nextAttempt: 0, createdAt: now, updatedAt: now };
    const fileRecord: FileRecord = { id: job.id, original: files };
    // The camera result is retained even when the durable write fails.
    if (!updateDraft) { this.unsafe.set(job.id, { job: { ...job, state: 'saving' }, files: fileRecord }); this.publish(); }
    try {
      await this.storage.edit((jobs, blobs) => {
        const existing = jobs.get(job.id);
        if (existing) {
          this.assertScope(existing, feature);
          if (!updateDraft || existing.state !== 'draft' || !input.draft || existing.type !== input.type) throw new Error('Dieser Auftrag wurde bereits abgesendet.');
          job.createdAt = existing.createdAt;
          job.files = existing.files;
        }
        if (job.parentId) {
          const parent = jobs.get(job.parentId);
          this.assertScope(parent, feature);
          if (parent.state !== 'draft' || job.state !== 'draft') throw new Error('Dieser Entwurf wurde bereits abgesendet.');
        }
        jobs.set(job.id, job);
        if (!existing) blobs.put(fileRecord);
      });
      this.unsafe.delete(job.id);
      this.records = this.records.filter(item => item.id !== job.id).concat(job);
      this.publish();
      // A refresh failure after commit must not reclassify durable data as unsaved.
      await this.changed().catch(() => this.error.set('Gespeichert. Übertragungsstatus wird erneut geprüft.'));
      void this.pump();
      return job;
    } catch (error) {
      if (!updateDraft) {
        this.unsafe.set(job.id, { job: { ...job, state: 'local_error', error: 'Nicht auf dem Gerät gesichert. Bitte Seite offen lassen oder Datei herunterladen.' }, files: fileRecord });
        this.publish();
      }
      throw error;
    }
  }
  async release(feature: string, id: string, build: (root: Transfer, children: Transfer[]) => Json) {
    if (Array.from(this.unsafe.values()).some(item => item.job.owner === this.owner && (item.job.id === id || item.job.parentId === id))) throw new Error('Bitte zuerst ungesicherte Dateien sichern oder entfernen.');
    const epoch = this.epoch;
    const released = await this.storage.edit(jobs => {
      const root = jobs.get(id); this.assertScope(root, feature);
      if (root.state !== 'draft') throw new Error('Dieser Auftrag wurde bereits abgesendet.');
      const children = Array.from(jobs.values()).filter(job => job.parentId === id);
      const now = new Date().toISOString();
      const updates: Transfer[] = [];
      for (const child of children) {
        this.assertScope(child, feature);
        if (child.state !== 'draft') throw new Error('Entwurf wurde gleichzeitig verändert.');
        updates.push({ ...child, state: 'waiting', updatedAt: now });
      }
      updates.push({ ...root, data: copyData(build(structuredClone(root), structuredClone(children))), state: 'waiting', updatedAt: now });
      for (const job of updates) jobs.set(job.id, job);
      return updates;
    });
    // edit resolves only after commit. Publish that confirmed snapshot without
    // another read; an older in-flight refresh must not restore draft states.
    if (epoch === this.epoch) {
      this.refreshSequence++;
      const ids = new Set(released.map(job => job.id));
      this.records = this.records.filter(job => !ids.has(job.id)).concat(released);
      this.publish();
    }
    // Notification and background reads cannot turn a committed release into
    // a failed submission. The normal pump/timer reconciles and resumes it.
    try { this.env.changed(); } catch { /* Other tabs also reconcile periodically. */ }
    void this.pump();
  }
  async files(feature: string, id: string): Promise<FileRecord | undefined> {
    const unsafe = this.unsafe.get(id);
    const job = unsafe?.job || this.records.find(item => item.id === id);
    this.assertScope(job, feature);
    const result = unsafe?.files || await this.storage.files(id);
    if (job.owner !== this.owner) return undefined;
    return result;
  }
  async retry(feature: string, id: string) {
    const unsafe = this.unsafe.get(id);
    if (unsafe) {
      this.assertScope(unsafe.job, feature);
      await this.storage.edit((jobs, blobs) => {
        if (jobs.has(id)) throw new Error('Übertragung bereits gespeichert. Bitte neu laden.');
        const parent = unsafe.job.parentId ? jobs.get(unsafe.job.parentId) : undefined;
        if (unsafe.job.parentId && (!parent || parent.state !== 'draft')) throw new Error('Entwurf nicht mehr bearbeitbar. Datei bitte herunterladen.');
        jobs.set(id, { ...unsafe.job, state: parent ? 'draft' : 'waiting', error: undefined }); blobs.put(unsafe.files);
      });
      this.unsafe.delete(id);
    } else await this.storage.edit(jobs => {
      const job = jobs.get(id); this.assertScope(job, feature);
      if (live(job) || job.state === 'done' || job.state === 'draft') return;
      jobs.set(id, { ...job, state: job.receivedAt ? 'processing' : 'waiting', error: undefined, nextAttempt: 0 });
    });
    await this.changed(); void this.pump(true);
  }
  async remove(feature: string, id: string) {
    const unsafe = this.unsafe.get(id);
    if (unsafe) { this.assertScope(unsafe.job, feature); this.unsafe.delete(id); this.publish(); return; }
    const adapter = (await this.loaders[feature]?.())?.default;
    await this.storage.edit((jobs, blobs) => {
      const job = jobs.get(id); this.assertScope(job, feature);
      if (live(job)) throw new Error('Übertragung läuft gerade. Bitte kurz warten.');
      if (job.receivedAt || job.state === 'done') throw new Error('Bereits empfangene Daten werden hier nicht gelöscht.');
      if (Array.from(jobs.values()).some(item => item.parentId === id)) throw new Error('Bitte zuerst die zugehörigen Dateien entfernen.');
      if (job.state === 'draft' || (!job.sendStarted && !job.parentId)) { jobs.delete(id); blobs.delete(id); }
      else {
        if (!adapter?.[job.type]?.cancel) throw new Error('Empfang noch unklar. Bitte erneut versuchen, damit der Serverstatus geprüft wird.');
        jobs.set(id, { ...job, cancelRequested: true, state: 'waiting', nextAttempt: 0, error: undefined });
      }
    });
    await this.changed(); void this.pump(true);
  }

  /** Explicit opt-in: stop retrying locally, without promising a server rollback.
   * cancel keeps only metadata for an adapter's existing cancellation contract.
   * Unlike remove(), this operation must work offline and never load an adapter.
   */
  async discard(feature: string, id: string, cleanup: 'none' | 'cancel' = 'none') {
    const unsafe = this.unsafe.get(id);
    if (unsafe) { this.assertScope(unsafe.job, feature); this.unsafe.delete(id); this.publish(); return; }
    const epoch = this.epoch;
    const remaining = await this.storage.edit((jobs, blobs) => {
      const job = jobs.get(id); this.assertScope(job, feature);
      if (Array.from(jobs.values()).some(item => item.parentId === id)) throw new Error('Bitte zuerst die zugehörigen Dateien entfernen.');
      // A concurrent receipt is harmless: forget the local entry, never the server data.
      const needsCleanup = cleanup === 'cancel' && job.state !== 'draft' && !job.receivedAt && job.state !== 'done';
      const next: Transfer | undefined = needsCleanup ? { ...job, discardedAt: Date.now(), cancelRequested: true,
        state: 'waiting', lease: undefined, leaseUntil: 0, nextAttempt: 0, progress: null, error: undefined,
        updatedAt: new Date().toISOString() } : undefined;
      if (next) jobs.set(id, next); else jobs.delete(id);
      blobs.delete(id);
      return next;
    });
    // Invalidate the lease before aborting. Late progress, receipts and failures
    // (including those from another tab) cannot restore the discarded upload.
    this.active.get(id)?.abort();
    if (epoch === this.epoch) {
      this.refreshSequence++;
      this.records = this.records.filter(job => job.id !== id).concat(remaining ? [remaining] : []);
      this.publish();
    }
    try { this.env.changed(); } catch { /* Other tabs also reconcile periodically. */ }
    void this.pump();
  }

  private async claim(selected: Transfer) {
    const token = crypto.randomUUID();
    return this.storage.edit(jobs => {
      const job = jobs.get(selected.id);
      if (!job || job.owner !== this.owner || !eligible(job) || live(job)) return undefined;
      if (job.parentId && jobs.get(job.parentId)?.state !== 'done') return undefined;
      const claimed: Transfer = { ...job, lease: token, leaseUntil: Date.now() + 60000, state: job.receivedAt ? 'processing' : 'preparing', attempts: job.attempts + 1 };
      jobs.set(job.id, claimed); return claimed;
    });
  }
  private async update(job: Transfer, patch: Partial<Transfer>, releaseFiles = false) {
    return this.storage.edit((jobs, blobs) => {
      const current = jobs.get(job.id);
      if (current?.lease !== job.lease || current.owner !== job.owner) return false;
      jobs.set(job.id, { ...current, ...patch, updatedAt: new Date().toISOString() });
      if (releaseFiles) blobs.delete(job.id);
      return true;
    });
  }
  private async process(selected: Transfer) {
    // Other tabs may enqueue types introduced after this UI was loaded. Do not
    // claim or mutate those jobs; a tab with the matching adapter handles them.
    const loader = this.loaders[selected.feature];
    if (!loader) return;
    let adapter: TransferAdapter;
    try {
      adapter = (await loader()).default[selected.type];
      if (!adapter) return;
    } catch {
      this.error.set('Übertragung konnte noch nicht geladen werden. Bitte Verbindung prüfen oder Seite neu laden.');
      return;
    }
    const owner = await this.authenticate();
    if (owner !== selected.owner) return;
    const job = await this.claim(selected);
    if (!job) return;
    if (job.owner !== this.owner) {
      await this.update(job, { lease: undefined, leaseUntil: 0, state: job.receivedAt ? 'processing' : 'waiting' });
      return;
    }
    const epoch = this.epoch;
    const controller = new AbortController(); this.active.set(job.id, controller);
    const valid = () => this.epoch === epoch && !controller.signal.aborted;
    const heartbeat = setInterval(() => {
      void this.update(job, { leaseUntil: Date.now() + 60000 }).then(owned => { if (!owned) controller.abort(); }).catch(() => controller.abort());
    }, 10000);
    try {
      await this.changed();
      const parent = this.records.find(item => item.id === job.parentId);
      const context: TransferContext = {
        signal: controller.signal, parent,
        files: async () => {
          const stored = await this.storage.files(job.id);
          if (!stored || !valid()) throw new TransferError('Datei nicht verfügbar. Bitte Übertragung prüfen.', false);
          // WebKit can send an empty multipart body for a File restored from
          // IndexedDB (https://bugs.webkit.org/show_bug.cgi?id=319985). A fresh
          // wrapper uses its Blob contents instead of that stored file's path.
          // Do this after every read, without re-encoding or persisting it again.
          return (stored.prepared || stored.original).map(file => new File([file], file.name, {
            type: file.type, lastModified: file.lastModified,
          }));
        },
        progress: (progress, state = 'uploading') => {
          if (!valid()) return;
          this.records = this.records.map(item => item.id === job.id ? { ...item, progress, state } : item);
          this.publish();
          this.env.progress?.({ id: job.id, owner: job.owner, lease: job.lease, progress, state });
        },
      };
      let receipt: Receipt;
      if (job.cancelRequested) {
        if (!adapter.cancel) throw new TransferError('Diese Übertragung kann nicht sicher zurückgezogen werden. Bitte erneut versuchen oder das Ergebnis im Feature prüfen.', false);
        receipt = await adapter.cancel(job, context);
      } else receipt = await adapter.inspect(job, context);
      if (!valid()) throw new TransferError('Übertragung angehalten.');
      if (receipt.cancelled || (job.discardedAt && receipt.received)) {
        await this.storage.edit((jobs, blobs) => { if (jobs.get(job.id)?.lease === job.lease) { jobs.delete(job.id); blobs.delete(job.id); } });
        return;
      }
      if (job.discardedAt) throw new TransferError('Entfernung wird noch abgeglichen.');
      if (!receipt.received) {
        if (job.receivedAt) throw new TransferError('Serverbestätigung derzeit nicht verfügbar. Datei wird nicht erneut übertragen.');
        if (adapter.prepare && !job.prepared && job.files.length) {
          const stored = await this.storage.files(job.id);
          if (!stored) throw new TransferError('Datei nicht verfügbar.', false);
          const prepared = await adapter.prepare(stored.original, job);
          if (!valid()) throw new TransferError('Übertragung angehalten.');
          const saved = await this.storage.edit((jobs, blobs) => {
            if (jobs.get(job.id)?.lease !== job.lease) return false;
            blobs.put({ ...stored, prepared }); jobs.set(job.id, { ...jobs.get(job.id)!, prepared: true }); return true;
          });
          if (!saved) throw new TransferError('Übertragung wurde übernommen.');
        }
        if (!valid()) throw new TransferError('Übertragung angehalten.');
        if (!await this.update(job, { sendStarted: true })) throw new TransferError('Übertragung wurde übernommen.');
        context.progress(null);
        receipt = await adapter.send(job, context);
      }
      if (!valid()) throw new TransferError('Übertragung angehalten.');
      if (!receipt.received) throw new TransferError('Empfang noch nicht bestätigt. Wird erneut geprüft.');
      const done = receipt.complete !== false;
      const patch: Partial<Transfer> = { state: done ? 'done' : 'processing', result: receipt.result ?? job.result ?? null,
        receivedAt: job.receivedAt || Date.now(), completedAt: done ? Date.now() : undefined, progress: 100,
        lease: undefined, leaseUntil: 0, cancelRequested: false, error: undefined, nextAttempt: done ? 0 : Date.now() + 5000 };
      if (await this.update(job, patch, true)) {
        this.error.set('');
        if (done) this.env.completed({ ...job, ...patch });
      }
    } catch (error) {
      const failure = error as TransferError;
      // Even an aborted tab releases only its own lease. The next run reconciles the receipt.
      await this.update(job, { state: failure.retryable === false ? 'error' : job.receivedAt ? 'processing' : 'waiting',
        error: failure.message || 'Übertragung unterbrochen.', progress: null, lease: undefined, leaseUntil: 0,
        cancelRequested: job.cancelRequested && (!!job.discardedAt || failure.retryable !== false),
        ...(failure.definitivelyRejected && !job.sendStarted ? { sendStarted: false } : {}),
        nextAttempt: Date.now() + Math.min(120000, 3000 * 2 ** Math.min(job.attempts - 1, 5)) + Math.random() * 1000 }).catch(() => {
        this.error.set('Status konnte nicht gespeichert werden. Bitte Dateien nicht entfernen.');
      });
    } finally {
      clearInterval(heartbeat); this.active.delete(job.id);
      await this.changed().catch(() => this.error.set('Übertragungsspeicher derzeit nicht verfügbar.'));
    }
  }
  async pump(resumed = false) {
    if (this.pumping) { this.requested = true; return; }
    if (!this.env.online()) return;
    this.pumping = true;
    try {
      // Authentication is checked only when work exists, plus once by the runtime at startup.
      await this.refresh();
      const attempted = new Set<string>();
      while (this.env.online()) {
        const candidates = this.records.filter(job => eligible(job) && !live(job) && !attempted.has(job.id)
          && (resumed || job.nextAttempt <= Date.now()) && (!job.parentId || this.records.some(parent => parent.id === job.parentId && parent.state === 'done')));
        if (!candidates.length) break;
        // Round-robin feature scheduling prevents a large backlog monopolising the queue.
        const selected = candidates.find(job => job.feature !== this.cursor) || candidates[0];
        attempted.add(selected.id); this.cursor = selected.feature;
        await this.process(selected);
      }
      await this.storage.edit((jobs, blobs) => {
        for (const job of Array.from(jobs.values())) if (job.owner === this.owner && job.state === 'done' && (job.completedAt || 0) < Date.now() - 86400000
          && !Array.from(jobs.values()).some(child => child.parentId === job.id && child.state !== 'done')) { jobs.delete(job.id); blobs.delete(job.id); }
      });
    } catch (error) { this.error.set((error as Error).message || 'Verbindung nicht verfügbar.'); }
    finally {
      this.pumping = false;
      if (this.requested) { this.requested = false; void this.pump(); }
    }
  }
}
